import type {
  RumActionEventDomainContext,
  RumEvent,
  RumEventDomainContext,
} from '@datadog/browser-rum'
import type { JsonObject } from '@seen/core/json'

export const APPOINTMENT_KIND_ATTRIBUTE = 'data-orbit-rum-appointment-kind'

// Keyed by the DOM event object itself, which correlates exactly with the
// events RUM hands back in the action's domain context — no timing heuristics,
// and entries die with the event they describe.
const appointmentKindByInteractionEvent = new WeakMap<Event, string>()

// `pointerup` is the only event RUM reports on an action, on every path:
// `trackClickActions` sets `events: domEvents ?? [startEvent]`,
// `listenActionEvents` sources `startEvent` from a `pointerup` listener, and
// `domEvents` is itself a map over those same pointerup events. Recording any
// other type — `click` in particular — would key an association that nothing
// can ever read back, and enrichment would silently fall through to the DOM
// walk this exists to replace. The test below pins the type.
export const RECORDED_INTERACTION_EVENT = 'pointerup'

function appointmentKindFromDom(
  target: EventTarget | null
): string | undefined {
  if (!(target instanceof Element)) {
    return undefined
  }
  return (
    target
      .closest(`[${APPOINTMENT_KIND_ATTRIBUTE}]`)
      ?.getAttribute(APPOINTMENT_KIND_ATTRIBUTE) ?? undefined
  )
}

// Resolves the kind while the interacted element is still in the document.
// FullCalendar destroys and rebuilds its event elements whenever a click
// changes the view or an in-flight appointment fetch resolves afterwards; the
// clicked node is orphaned below the attributed host about 700-900ms later,
// well before `beforeSend` runs. Reading the DOM at flush time therefore loses
// the kind on exactly those clicks, so it is captured here instead.
//
// Not feature-flag-gated, for the same reason as the walk below: instrument.ts
// runs before flag bootstrap, and while the flag is off the attribute is simply
// absent from the DOM, which makes this inert.
export function recordAppointmentKindOnInteraction(event: Event): void {
  const appointmentKind = appointmentKindFromDom(event.target)
  if (appointmentKind) {
    appointmentKindByInteractionEvent.set(event, appointmentKind)
  }
}

// Falls back to the DOM walk for actions whose interaction was never recorded —
// a listener registered after the event, or an action with no pointer event.
// Still correct whenever the element survives to flush time.
function readAppointmentKind(domEvent: Event): string | undefined {
  return (
    appointmentKindByInteractionEvent.get(domEvent) ??
    appointmentKindFromDom(domEvent.target)
  )
}

// Not feature-flag-gated: instrument.ts runs before flag bootstrap, so it has
// no way to read flag state. This walker is inert until the flag-gated
// `data-orbit-rum-appointment-kind` attribute actually exists in the DOM,
// which is exactly the flag-off state — so gating here would be redundant.
export function enrichActionEventWithAppointmentKind(
  event: RumEvent,
  context: RumEventDomainContext
): void {
  try {
    if (event.type !== 'action') {
      return
    }
    // `context`'s type is a union across every RUM event kind; narrowed to
    // the action-only shape now that `event.type` has been checked above.
    const actionContext = context as RumActionEventDomainContext
    const appointmentKind = actionContext.events
      ?.map(readAppointmentKind)
      .find((kind): kind is string => kind !== undefined)
    if (!appointmentKind) {
      return
    }

    const existingContext = event.context ?? {}
    const existingOrbit =
      (existingContext.orbit as JsonObject | undefined) ?? {}
    const existingInteraction =
      (existingOrbit.interaction as JsonObject | undefined) ?? {}
    event.context = {
      ...existingContext,
      orbit: {
        ...existingOrbit,
        interaction: {
          ...existingInteraction,
          appointment_kind: appointmentKind,
        },
      },
    }
  } catch {
    // Enrichment must never block event delivery.
  }
}
